import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { extraApi } from '../../lib/api';
import { getErrorMessage } from '../../lib/errorUtils';

const TYPE_LABEL = { lavoro: 'Lavoro', fornitura: 'Fornitura' };
const fmtEuro = (n) => (n == null ? '—' : `${Number(n).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);

export const ReclassifyDialog = ({ open, onOpenChange, extraId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [reason, setReason] = useState('');
  const [pf, setPf] = useState(null);

  useEffect(() => {
    if (!open || !extraId) return;
    setPf(null); setReason('');
    setLoading(true);
    extraApi.reclassifyPreflight(extraId)
      .then((res) => setPf(res.data))
      .catch((e) => { toast.error(getErrorMessage(e, 'Errore nel preflight')); onOpenChange(false); })
      .finally(() => setLoading(false));
  }, [open, extraId]); // eslint-disable-line

  const handleApply = async () => {
    if (!pf || !reason.trim()) return;
    setApplying(true);
    try {
      const res = await extraApi.reclassify(extraId, {
        new_extra_type: pf.target_type,
        reason: reason.trim(),
        expected_current_type: pf.current_type,
        confirmation: true,
      });
      const retired = res.data?.sal_retired?.length || 0;
      toast.success(`Classificazione corretta: ${TYPE_LABEL[pf.current_type]} → ${TYPE_LABEL[pf.target_type]}${retired ? ` · ${retired} voci SAL ritirate` : ''}`);
      onOpenChange(false);
      onSuccess && onSuccess();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Riclassificazione non consentita'));
    } finally {
      setApplying(false);
    }
  };

  const isSafe = pf?.is_safe;
  const ev = pf?.evidence || {};
  const supplierOrders = ev?.sal?.supplier_orders || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="reclassify-dialog">
        <DialogHeader>
          <DialogTitle>Correggi classificazione</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-10 text-[#737373]" data-testid="reclassify-loading">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Analisi in corso…
          </div>
        )}

        {!loading && pf && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-[#737373]">Tipo attuale:</span>
              <Badge variant="outline">{TYPE_LABEL[pf.current_type]}</Badge>
              <span className="text-[#737373]">→ Nuovo tipo:</span>
              <Badge className="bg-[#FF7819] text-white">{TYPE_LABEL[pf.target_type]}</Badge>
            </div>

            <div className={`rounded-lg p-3 border flex items-start gap-2 ${isSafe ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}
                 data-testid="reclassify-classification">
              {isSafe ? <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" /> : <XCircle className="w-5 h-5 text-red-600 mt-0.5" />}
              <div className="text-sm">
                <div className="font-semibold text-[#3C3C3C]">{pf.classification}</div>
                {!isSafe && <div className="text-red-700">Blocchi: {pf.blockers.join(', ') || '—'}</div>}
              </div>
            </div>

            <div className="text-sm space-y-1 bg-[#F7F7F5] rounded-lg p-3">
              <div className="font-medium text-[#3C3C3C] mb-1">Preview effetti</div>
              <div className="text-green-700">✓ Importo firmato invariato ({fmtEuro(pf.immutable?.total_with_vat)} lordo)</div>
              <div className="text-green-700">✓ PDF firmato, numero Extra e date invariati</div>
              <div className="text-green-700">✓ Contratto e QE invariati</div>
              <div className="text-[#3C3C3C]">
                Schedule 50/50 allineato: caparra {fmtEuro(pf.derived_changes?.after?.deposit_amount)} · saldo {fmtEuro(pf.derived_changes?.after?.final_amount)}
              </div>
              {ev?.sal?.non_executed_count > 0 && (
                <div className="text-[#3C3C3C]">SAL: {ev.sal.non_executed_count} voci non eseguite verranno ritirate dal perimetro SAL</div>
              )}
            </div>

            {supplierOrders.length > 0 && (
              <div className="rounded-lg p-3 border border-amber-300 bg-amber-50 flex items-start gap-2" data-testid="reclassify-supplier-warning">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-800">
                  È presente un ordine fornitore collegato:
                  {supplierOrders.map((o, i) => (
                    <div key={i} className="font-medium">#{o.order_number} — {o.order_supplier}</div>
                  ))}
                  <div className="text-xs mt-1">Non verrà modificato dalla riclassificazione.</div>
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="reclassify-reason">Motivo <span className="text-red-500">*</span></Label>
              <Textarea
                id="reclassify-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Es: le voci sono forniture (parquet/battiscopa), tipo errato in fase di inserimento"
                className="mt-1"
                data-testid="reclassify-reason-input"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reclassify-cancel-btn">Annulla</Button>
          <Button
            onClick={handleApply}
            disabled={!pf || !isSafe || !reason.trim() || applying}
            className="bg-[#FF7819] hover:bg-[#e56a12] text-white"
            data-testid="reclassify-confirm-btn"
          >
            {applying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Conferma correzione
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReclassifyDialog;
